import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Plus } from 'lucide-react';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';
import { showToast } from '../utils/toast';
import '../styles/AdminComponents.css';
import '../styles/UserManagement.css';

const ROLE_LABEL = { admin: 'Admin', supervisor: 'Supervisor', agent: 'Agente' };
const EMPTY_FORM = { name: '', email: '', password: '', role: 'agent', departmentIds: [] };

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Erro ao buscar departamentos:', error);
    }
  };

  const handleEdit = async (userId) => {
    try {
      const response = await api.get(`/users/${userId}`);
      const user = response.data;
      setFormData({
        id: user.id,
        name: user.name,
        email: user.email,
        password: '',
        role: user.role,
        departmentIds: user.departments || [],
      });
      setShowForm(true);
    } catch (error) {
      showToast('Erro ao carregar dados do usuário', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let userId;
      if (formData.id) {
        await api.put(`/users/${formData.id}`, formData);
        userId = formData.id;
        showToast('Usuário atualizado!', 'success');
      } else {
        const response = await api.post('/users', formData);
        userId = response.data.id;
        showToast('Usuário criado!', 'success');
      }
      await api.post(`/users/${userId}/assign-department`, { departmentIds: formData.departmentIds });
      resetForm();
      fetchUsers();
    } catch (error) {
      showToast(error.response?.data?.message || 'Erro ao salvar usuário', 'error');
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await api.delete(`/users/${id}`);
      showToast('Usuário excluído', 'success');
      fetchUsers();
    } catch (error) {
      showToast(error.response?.data?.message || 'Erro ao excluir usuário', 'error');
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setShowForm(false);
  };

  const toggleDepartment = (deptId) => {
    setFormData(prev => ({
      ...prev,
      departmentIds: prev.departmentIds.includes(deptId)
        ? prev.departmentIds.filter(id => id !== deptId)
        : [...prev.departmentIds, deptId],
    }));
  };

  return (
    <div className="admin-component">
      <div className="admin-header">
        <h2>Gerenciar Usuários</h2>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="add-btn">
          <Plus size={20} />
          Novo Usuário
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="admin-form">
          <input
            type="text"
            placeholder="Nome"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <input
            type="password"
            placeholder={formData.id ? 'Nova senha (deixe vazio para não alterar)' : 'Senha'}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required={!formData.id}
          />
          <select
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
          >
            <option value="agent">Agente</option>
            <option value="supervisor">Supervisor</option>
            <option value="admin">Admin</option>
          </select>

          {departments.length > 0 && (
            <div className="department-checkboxes">
              <label><strong>Departamentos</strong></label>
              {departments.map(dept => (
                <label key={dept.id} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.departmentIds.includes(dept.id)}
                    onChange={() => toggleDepartment(dept.id)}
                  />
                  {dept.name}
                </label>
              ))}
            </div>
          )}

          <button type="submit" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar'}
          </button>
          <button type="button" onClick={resetForm}>
            Cancelar
          </button>
        </form>
      )}

      {/* Desktop */}
      <div className="um-table-wrap">
        <table className="users-table">
          <thead>
            <tr><th>Nome</th><th>Email</th><th>Função</th><th>Status</th><th>Ações</th></tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{ROLE_LABEL[user.role] || user.role}</td>
                <td>{user.active ? 'Ativo' : 'Inativo'}</td>
                <td className="actions">
                  <button className="edit-btn" onClick={() => handleEdit(user.id)}><Edit2 size={16}/></button>
                  <button className="delete-btn" onClick={() => setConfirmDeleteId(user.id)}><Trash2 size={16}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile */}
      <div className="um-cards">
        {users.map(user => (
          <div key={user.id} className="um-card">
            <div className="um-card-top">
              <div>
                <div className="um-card-name">{user.name}</div>
                <div className="um-card-email">{user.email}</div>
              </div>
              <div className="um-card-actions">
                <button className="edit-btn" onClick={() => handleEdit(user.id)}><Edit2 size={16}/></button>
                <button className="delete-btn" onClick={() => setConfirmDeleteId(user.id)}><Trash2 size={16}/></button>
              </div>
            </div>
            <div className="um-card-meta">
              <span className="um-badge">{ROLE_LABEL[user.role] || user.role}</span>
              <span className={`um-status ${user.active ? 'active' : 'inactive'}`}>{user.active ? '● Ativo' : '● Inativo'}</span>
            </div>
          </div>
        ))}
      </div>

      {confirmDeleteId && (
        <ConfirmModal
          title="Excluir usuário"
          message="Tem certeza? O usuário será removido permanentemente. Conversas atribuídas a ele voltarão para a fila."
          confirmLabel="Excluir"
          onConfirm={doDelete}
          onCancel={() => setConfirmDeleteId(null)}
          danger
        />
      )}
    </div>
  );
}

export default UserManagement;
